"use client";

import { useEffect } from "react";
import Link from "next/link";
import { useUI } from "@/lib/context/UIProvider";
import { useLocale } from "@/lib/context/LocaleProvider";
import { useAuth } from "@/lib/context/AuthProvider";
import { usePersonalization } from "@/lib/context/PersonalizationProvider";
import { useSidebarRecents } from "@/lib/hooks/useSidebarRecents";
import { historyItemListLabels } from "@/lib/api/generations";
import type { BgTheme } from "@/lib/data/backgrounds";
import { cn } from "@/lib/utils";
import { Check, History, ShieldCheck } from "@/components/ui/icons";

const MENU_ITEM =
  "flex w-full items-center gap-[15px] rounded-[15px] px-3 py-[11px] text-left text-sm font-medium tracking-[-0.1px] text-ink-4 transition-colors hover:bg-[rgba(26,24,22,0.05)]";
const MENU_ICON = "flex w-[22px] flex-none items-center justify-center text-muted-2";
const DIVIDER = "mx-2 my-2.5 h-px bg-[rgba(26,24,22,0.07)]";

function tileBackground(theme: BgTheme): string {
  if (theme.type === "img") return `url('${theme.img}') center 78% / cover no-repeat`;
  if (theme.type === "color") return theme.swatch;
  return "#ffffff";
}

/** Pulse rows that mirror a Recents list item (dot + title + subtitle). */
function RecentsSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="flex flex-col" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <div
          key={i}
          className="flex w-full items-center gap-[11px] px-3 py-[9px]"
        >
          <span className="h-1.5 w-1.5 flex-none animate-pulse rounded-full bg-[rgba(26,24,22,0.12)]" />
          <span className="block min-w-0 flex-1 space-y-1.5">
            <span
              className="block h-[13px] animate-pulse rounded-[5px] bg-[rgba(26,24,22,0.1)]"
              style={{ width: `${72 - i * 8}%` }}
            />
            <span
              className="block h-[11px] animate-pulse rounded-[4px] bg-[rgba(26,24,22,0.06)]"
              style={{ width: `${48 - i * 4}%` }}
            />
          </span>
        </div>
      ))}
    </div>
  );
}

function BgTile({
  theme,
  index,
  active,
  onSelect,
}: {
  theme: BgTheme;
  index: number;
  active: boolean;
  onSelect: (index: number) => void;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-[5px]">
      <button
        type="button"
        title={theme.label}
        aria-label={theme.label}
        onClick={() => onSelect(index)}
        className={cn(
          "relative flex aspect-[4/3] w-full items-center justify-center rounded-[10px] p-0 transition hover:opacity-85",
          active ? "border-2 border-accent" : "border border-[rgba(26,24,22,0.12)]",
        )}
        style={{
          background: tileBackground(theme),
          boxShadow: active
            ? "0 0 0 2px color-mix(in srgb, var(--accent) 20%, transparent)"
            : undefined,
        }}
      >
        {active ? (
          <span className="flex h-4 w-4 items-center justify-center rounded-full bg-accent text-white shadow-[0_1px_3px_rgba(26,24,22,0.3)]">
            <Check size={9} />
          </span>
        ) : theme.type === "none" ? (
          <span className="flex items-center justify-center text-muted-3">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.9"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="8.4" />
              <path d="M6.2 6.2l11.6 11.6" />
            </svg>
          </span>
        ) : null}
      </button>
      <span
        className={cn(
          "truncate text-center text-[10.5px] font-semibold",
          active ? "text-accent" : "text-muted-2",
        )}
      >
        {theme.label}
      </span>
    </div>
  );
}

/** Slide-in navigation drawer: profile, recents, personalization, links. */
export function Sidebar() {
  const { sidebarOpen, closeSidebar } = useUI();
  const { t } = useLocale();
  const { user, isAuthenticated, isPro, logout } = useAuth();
  const { themes, bgIndex, setBgIndex } = usePersonalization();
  const {
    items: recentItems,
    hasMore,
    loading: recentsLoading,
    loaded: recentsLoaded,
  } = useSidebarRecents(Boolean(sidebarOpen && isAuthenticated));

  // Defensive close: the drawer is only ever opened for signed-in users
  // (see Header), but if a session ends while it's open, drop it too.
  useEffect(() => {
    if (sidebarOpen && !isAuthenticated) closeSidebar();
  }, [sidebarOpen, isAuthenticated, closeSidebar]);

  if (!isAuthenticated) return null;

  const primaryEmail =
    user?.identities.find((i) => i.provider === "password")?.identifier ??
    user?.identities[0]?.identifier ??
    null;
  const displayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim() ||
    primaryEmail ||
    t.accountTitle;
  const showEmailLine = Boolean(primaryEmail) && primaryEmail !== displayName;

  const handleSignOut = () => {
    closeSidebar();
    void logout();
  };

  return (
    <aside
      className={cn(
        "fixed inset-y-0 left-0 z-[70] box-border border-r border-[rgba(26,24,22,0.06)] bg-surface-2 transition-[transform,box-shadow] duration-[440ms] max-md:w-[76vw] md:w-[280px]",
        "[transition-timing-function:cubic-bezier(.22,1,.36,1)]",
        sidebarOpen ? "translate-x-0" : "-translate-x-full",
      )}
      style={{
        boxShadow: sidebarOpen ? "0 0 70px -12px rgba(45,32,50,0.22)" : "none",
      }}
    >
      <div className="flex h-full flex-col overflow-y-auto px-4 pb-[calc(18px_+_env(safe-area-inset-bottom))] pt-[calc(22px_+_env(safe-area-inset-top))]">
        {/* Profile */}
        <div className="flex items-center gap-[13px] px-2 pt-0.5">
          <div
            className="relative flex h-[46px] w-[46px] flex-none items-center justify-center rounded-full bg-accent-soft text-accent"
            style={{ boxShadow: "0 0 0 2px #ffffff, 0 0 0 3.5px #D9B65C" }}
          >
            <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <circle cx="12" cy="8" r="4" />
              <path d="M4 20c0-4 3.6-6.5 8-6.5s8 2.5 8 6.5z" />
            </svg>
            <span className="absolute -bottom-[5px] left-1/2 -translate-x-1/2 rounded-full border-[1.5px] border-surface-2 bg-accent px-1.5 py-0.5 text-[8px] font-bold leading-none tracking-[0.5px] text-white shadow-[0_1px_2px_rgba(26,24,22,0.18)]">
              {isPro ? "PRO" : "FREE"}
            </span>
          </div>
          <div className="min-w-0">
            <div className="truncate text-base font-semibold tracking-[-0.3px] text-ink">
              {displayName}
            </div>
            {showEmailLine ? (
              <div className="truncate text-[12.5px] text-muted">{primaryEmail}</div>
            ) : null}
          </div>
        </div>

        <div className="mx-2 mb-2.5 mt-5 h-px bg-[rgba(26,24,22,0.07)]" />

        {/* Recents — latest 5 from GET /v1/generation */}
        <div className="px-3 pb-1.5 text-[11px] font-bold uppercase tracking-[0.7px] text-muted-2">
          {t.menuRecents}
        </div>
        <div className="-mx-1 flex-none px-1 pb-0.5">
          {!recentsLoaded || (recentsLoading && recentItems.length === 0) ? (
            <RecentsSkeleton count={3} />
          ) : recentItems.length === 0 ? (
            <div className="flex flex-col items-center gap-2.5 px-3 py-5 text-center">
              <span className="flex h-9 w-9 items-center justify-center rounded-[12px] bg-accent-soft text-accent">
                <History size={18} />
              </span>
              <p className="text-[12.5px] leading-[1.4] text-muted [text-wrap:pretty]">
                {t.menuRecentsEmpty}
              </p>
            </div>
          ) : (
            <>
              {recentItems.map((item) => {
                const { title, subtitle } = historyItemListLabels(item);
                return (
                  <Link
                    key={item.slug}
                    href={`/history/${item.slug}`}
                    onClick={closeSidebar}
                    className="flex w-full items-center gap-[11px] rounded-[13px] px-3 py-[9px] text-left transition-colors hover:bg-[rgba(26,24,22,0.05)]"
                  >
                    <span className="h-1.5 w-1.5 flex-none rounded-full bg-accent" />
                    <span className="block min-w-0 flex-1">
                      <span className="block truncate text-[13.5px] font-semibold tracking-[-0.15px] text-ink">
                        {title}
                      </span>
                      {subtitle ? (
                        <span className="block truncate text-[11.5px] text-muted">
                          {subtitle}
                        </span>
                      ) : null}
                    </span>
                  </Link>
                );
              })}
              {hasMore ? (
                <Link
                  href="/history"
                  onClick={closeSidebar}
                  className="mt-0.5 flex w-full items-center justify-center rounded-[13px] px-3 py-[10px] text-[13px] font-semibold tracking-[-0.1px] text-accent transition-colors hover:bg-[rgba(26,24,22,0.05)]"
                >
                  {t.menuRecentsShowMore}
                </Link>
              ) : null}
            </>
          )}
        </div>

        <div className="mx-2 mb-2.5 mt-auto h-px bg-[rgba(26,24,22,0.07)]" />

        {/* Personalize */}
        <div className="px-3 pb-2 text-[11px] font-bold uppercase tracking-[0.7px] text-muted-2">
          {t.menuPersonalize}
        </div>
        <div className="grid grid-cols-4 gap-2 px-3 pb-[9px]">
          {themes.slice(0, 4).map((theme, i) => (
            <BgTile
              key={theme.label}
              theme={theme}
              index={i}
              active={bgIndex === i}
              onSelect={setBgIndex}
            />
          ))}
        </div>
        <div className="grid grid-cols-4 gap-2 px-3 pb-1.5">
          {themes.slice(4).map((theme, i) => (
            <BgTile
              key={theme.label}
              theme={theme}
              index={i + 4}
              active={bgIndex === i + 4}
              onSelect={setBgIndex}
            />
          ))}
        </div>

        <div className={DIVIDER} style={{ marginTop: 10, marginBottom: 10 }} />

        {/* Links */}
        <Link href="/account" onClick={closeSidebar} className={MENU_ITEM}>
          <span className={MENU_ICON}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1.04 1.56V21a2 2 0 1 1-4 0v-.09A1.7 1.7 0 0 0 9 19.35a1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.65 15a1.7 1.7 0 0 0-1.56-1.04H3a2 2 0 1 1 0-4h.09A1.7 1.7 0 0 0 4.65 9a1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 9 4.65a1.7 1.7 0 0 0 1.04-1.56V3a2 2 0 1 1 4 0v.09A1.7 1.7 0 0 0 15 4.65a1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.7 1.7 0 0 0 19.35 9a1.7 1.7 0 0 0 1.56 1.04H21a2 2 0 1 1 0 4h-.09A1.7 1.7 0 0 0 19.4 15z" />
            </svg>
          </span>
          <span>{t.menuAccountSettings}</span>
        </Link>
        <Link
          href="/certificates/verify"
          onClick={closeSidebar}
          className={MENU_ITEM}
        >
          <span className={MENU_ICON}>
            <ShieldCheck size={20} />
          </span>
          <span>{t.certsVerifyTitle}</span>
        </Link>
        <Link href="/about" onClick={closeSidebar} className={MENU_ITEM}>
          <span className={cn(MENU_ICON, "text-accent")}>
            <img
              src="/favicon.png"
              alt=""
              width={20}
              height={20}
              className="block h-5 w-5 rounded-md"
            />
          </span>
          <span>{t.menuAbout}</span>
        </Link>
        <a
          href="/docs/privacy.pdf"
          target="_blank"
          rel="noopener noreferrer"
          onClick={closeSidebar}
          className={MENU_ITEM}
        >
          <span className={MENU_ICON}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 3l7 3v5c0 4.5-3 7.7-7 9-4-1.3-7-4.5-7-9V6z" />
            </svg>
          </span>
          <span>{t.menuPrivacy}</span>
        </a>
        <a
          href="/docs/terms.pdf"
          target="_blank"
          rel="noopener noreferrer"
          onClick={closeSidebar}
          className={MENU_ITEM}
        >
          <span className={MENU_ICON}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M7 3h7l4 4v14H7z" />
              <path d="M14 3v4h4" />
              <path d="M9.5 13h5" />
              <path d="M9.5 16.5h5" />
            </svg>
          </span>
          <span>{t.menuTerms}</span>
        </a>
        <a href="mailto:info@nurse.tools" className={MENU_ITEM}>
          <span className={MENU_ICON}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="5" width="18" height="14" rx="2.5" />
              <path d="M3.5 7.5l8.5 6 8.5-6" />
            </svg>
          </span>
          <span>{t.menuContact}</span>
        </a>

        <div className={DIVIDER} />

        <button
          type="button"
          onClick={handleSignOut}
          className="flex w-full items-center gap-[15px] rounded-[15px] px-3 py-[13px] text-left text-[14.5px] font-semibold tracking-[-0.2px] text-danger transition-colors hover:bg-[rgba(179,73,46,0.06)]"
        >
          <span className="flex w-[22px] flex-none items-center justify-center">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M15 4h4v16h-4" />
              <path d="M10 8l-4 4 4 4" />
              <path d="M6 12h9" />
            </svg>
          </span>
          <span>{t.signOut}</span>
        </button>
      </div>
    </aside>
  );
}
